import {
  type CompilerError,
  type Document,
  getInvalidAttributeDescriptionErrors,
  hasInvalidAttributeDescription,
  INFER_INSTANCE_COMMAND,
  INFER_INSTANCES_COMMAND,
  normalizeText,
  type Relationship,
} from "@packages/commons";
import { isEqual } from "lodash";
import findLastIndex from "lodash/findLastIndex";
import kebabCase from "lodash/kebabCase";
import { v4 as uuid } from "uuid";
import {
  type MultiRegExpMatch,
  SEPARATOR_REGEX,
  COMMANDS,
  NOT_COMMAND,
  type Command,
  escapeRegExp,
} from "@imminently/decisively-core";
import {
  type Attribute,
  type AttributeMeta,
  type AttributesOption,
  type CompilerContext,
  createCompilerContext,
  iterateExpressions,
  PROCESSED_CHAR,
} from "./Compiler";
import { createMissingRelationshipError } from "./CompilerErrors";
import { COMPILER_ERROR_TYPES } from "@imminently/decisively-core";
import type CompilerScope from "./CompilerScope";
import { ParserCompilerScope } from "./CompilerScope";
import { type Token, TokenQueue } from "./Tokenizer";

export interface BaseExpression<T extends string = any> {
  id: string;
  type: T;
  operator?: Operator;
  start: number;
  end: number;
  errors?: CompilerError[];
  alias?: Alias;
}

export interface LiteralExpression extends BaseExpression<"literal"> {
  value: any;
  quoted: boolean;
  /** The literal as written, without quotes */
  text?: string;
}

export interface CommandExpression extends BaseExpression<"command"> {
  command: Command;
  arguments: Expression[];
  natural: boolean;
  relationshipId?: string;
  relationshipReverse?: boolean;
}

export interface RelationshipExpression extends BaseExpression<"relationship"> {
  relationshipId: string;
  relationshipReverse: boolean;
  text: string;
}

export interface AttributeExpression extends BaseExpression<"attribute"> {
  attributeId: string;
  text: string;
}

export interface Alias {
  relationshipId: string;
  text: string;
}

export interface AliasExpression extends BaseExpression<"alias"> {
  text: string;
  alias: Alias;
}

export interface CommentExpression extends BaseExpression<"comment"> {
  text: string;
}

export type Expression =
  | LiteralExpression
  | CommandExpression
  | RelationshipExpression
  | AttributeExpression
  | AliasExpression
  | CommentExpression;

const OPERATORS = ["and", "if", "or"] as const;
export type Operator = (typeof OPERATORS)[number];

let expressionId = 0;
export const generateExpressionId = (): string => {
  return `${expressionId++}`;
};

export const findCommand = (text: string): Command | undefined => {
  const processedText = kebabCase(text);
  const processedTextNoSeparator = processedText.replace(SEPARATOR_REGEX, "");

  return COMMANDS.find(
    (command) =>
      command.names.has(processedText) ||
      command.names.has(processedTextNoSeparator),
  );
};

const findContainmentRelationshipById = (
  context: CompilerContext,
  id: string,
): Relationship | undefined => {
  let relationship = context.relationships.find(
    (relationship) => relationship.id === id,
  );
  if (!relationship) {
    return undefined;
  }
  while (!relationship.containment) {
    relationship = context.relationships.find(
      (other) => other.target === relationship?.target,
    );
    if (!relationship) {
      return undefined;
    }
  }
  return relationship;
};

export interface AliasMatch {
  alias: Alias;
  aliasedAttribute: Attribute;
}

// Strips brackets that wrap the whole text, e.g. "((x))" -> "x". Brackets that only wrap part of the
// text are kept, so "(x) - 1" stays as is rather than losing the "- 1" (ENG-18).
const removeMatchingBrackets = (text: string): string => {
  while (true) {
    text = text.trim();
    if (!text.startsWith("(") || !text.endsWith(")")) {
      break;
    }
    // the opening bracket must close at the very end, otherwise it only wraps part of the text
    let depth = 0;
    let closesAtEnd = false;
    for (let i = 0; i < text.length; i++) {
      if (text[i] === "(") {
        depth++;
      } else if (text[i] === ")") {
        depth--;
        if (depth === 0) {
          closesAtEnd = i === text.length - 1;
          break;
        }
      }
    }
    if (!closesAtEnd) {
      break;
    }
    text = text.slice(1, -1);
  }
  return text;
};

const matchAlias = (
  context: CompilerContext,
  scope: CompilerScope,
  text: string,
): AliasMatch | undefined => {
  const aliases = Object.values(scope.getAliases());
  const matchingAliases = aliases.filter((a) =>
    text.toLowerCase().includes(a.text),
  );
  const alias = matchingAliases[0];
  if (alias) {
    const relationship = findContainmentRelationshipById(
      context,
      alias.relationshipId,
    );
    if (!relationship?.textSingular || !relationship.name) {
      return;
    }

    const newDescription = text.replace(
      new RegExp(escapeRegExp(alias.text), "g"),
      relationship.textSingular,
    );
    let aliasedAttribute = scope.getAttributeByDescription(
      newDescription,
      context.namespace,
    );
    if (!aliasedAttribute) {
      aliasedAttribute = {
        id: getGuidForDescription(context, newDescription),
        description: newDescription,
        entity: relationship.name,
        generated: true,
      };
    }
    aliasedAttribute.entity = relationship.name;
    return { alias, aliasedAttribute };
  }
  return undefined;
};

/**
 * Namespaced attributes are excluded even when disableGuids is on: a description-derived id
 * ignores namespace, so two attributes sharing a description in different namespaces (e.g. the
 * namespace-fork in GraphBuilder's flushConclusionToGraph) would otherwise collide onto the same
 * node. Namespaced compiles keep minting real GUIDs until that's solved separately.
 */
export const getGuidForDescription = (
  context: CompilerContext,
  description: string,
) => {
  if (context.disableGuids && !context.namespace) {
    return normalizeText(description);
  }
  return context.guidMap[normalizeText(description)] ?? uuid();
};

export const createAttributeExpression = (
  attribute: Attribute,
  context: CompilerContext,
  scope: ParserCompilerScope,
  tokens: Token[],
  connector: Operator | undefined,
  aliased?: boolean,
): Expression => {
  if (attribute.description === undefined) {
    throw new Error("Expected attribute to have a description");
  }

  if (!aliased) {
    const aliasMatch = matchAlias(context, scope, attribute.description);
    if (aliasMatch) {
      if (aliasMatch.aliasedAttribute.description === attribute.description) {
        throw new Error("Expected aliased attribute to be different");
      }
      const attributeExpression = createAttributeExpression(
        aliasMatch.aliasedAttribute,
        context,
        scope,
        tokens,
        connector,
        true,
      );
      attributeExpression.alias = aliasMatch.alias;
      return attributeExpression;
    }
  }

  scope.addAttribute(attribute);

  const errors = getInvalidAttributeDescriptionErrors(attribute.description);

  const attributeExpression: AttributeExpression = {
    id: generateExpressionId(),
    type: "attribute",
    attributeId: attribute.id,
    operator: connector,
    text: attribute.description,
    start: getTokenStartEnd(tokens[0]).start,
    end: getTokenStartEnd(tokens[tokens.length - 1]).end,
    errors: errors.length > 0 ? errors : undefined,
  };

  if (attribute.form === "negative") {
    return {
      id: generateExpressionId(),
      type: "command",
      command: NOT_COMMAND,
      operator: connector,
      start: attributeExpression.start,
      end: attributeExpression.end,
      natural: true,
      arguments: [attributeExpression],
    };
  }

  return attributeExpression;
};

const isLiteralToken = (token: Token): boolean => {
  return (
    token.type === "number" ||
    token.type === "quoted_phrase" ||
    token.type === "boolean" ||
    token.type === "date" ||
    token.type === "time" ||
    token.type === "uncertain"
  );
};

const trimWhitespace = (tokens: Token[]): Token[] => {
  const start = tokens.findIndex((token) => token.type !== "ws");
  if (start === -1) {
    return [];
  }

  let end = tokens.length - 1;
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (tokens[i].type !== "ws") {
      end = i;
      break;
    }
  }
  return tokens.slice(start, end + 1);
};

export const extractAttributeDescription = (
  context: CompilerContext,
  text: string,
): string => {
  const tokens = context.tokenizer.tokenize(text);

  let commentPairs = 0;

  let description = "";

  for (const token of tokens) {
    if (token.type === "start_comment") {
      commentPairs++;
      continue;
    }
    if (token.type === "end_comment") {
      if (commentPairs > 0) {
        commentPairs--;
        continue;
      }
    }

    if (commentPairs > 0) {
      continue;
    }
    description += token.text;
  }
  return normalizeText(description);
};

interface TokenBufferToExpressionResult {
  errors: CompilerError[];
  expression: Expression;
  scope: ParserCompilerScope;
}

interface CommandState {
  command: Command;
  relationshipId?: string;
  relationshipReverse?: boolean;
  arguments: Expression[];
}

const addCommandArguments = (
  state: CommandState,
  scope: CompilerScope,
  expressions: Expression[],
) => {
  const initialArgumentCount = state.arguments.length;
  //console.trace(state, expressions);
  state.arguments.push(
    ...expressions.map((expression, index) => {
      const argumentIndex = initialArgumentCount + index;
      const argumentDefinition = state.command.arguments[argumentIndex];

      if (expression.type === "relationship") {
        if (!state.relationshipId) {
          state.relationshipId = expression.relationshipId;
          state.relationshipReverse = expression.relationshipReverse;
        }
      }

      if (argumentDefinition?.types.includes("alias") && state.relationshipId) {
        if (expression.alias) {
          if (expression.alias.relationshipId !== state.relationshipId) {
            throw new Error(
              "Expected alias to have the same relationship as the current relationship",
            );
          }
          expression.type = "alias";
          // @ts-ignore
          expression.attributeId = undefined;
        } else {
          if (expression.type === "attribute") {
            scope.removeAttributeById(expression.attributeId);

            const text = expressionToText(expression);
            const alias: Alias | undefined =
              state.relationshipId && text
                ? {
                    text: text,
                    relationshipId: state.relationshipId,
                  }
                : undefined;
            Object.assign(expression, {
              type: "alias",
              attributeId: undefined,
              alias: alias,
            });
            if (alias) {
              scope.addAlias(alias);
            }
          }
        }
      }

      return expression;
    }),
  );
};

const tokenBufferToExpression = (
  parentScope: ParserCompilerScope,
  tokenBuffer: Token[],
  context: CompilerContext,
  state: ParseState,
  attributeMeta?: AttributeMeta,
  isCommandArgument: boolean = false,
): TokenBufferToExpressionResult | undefined => {
  let connector: Operator | undefined;
  if (
    tokenBuffer.length > 0 &&
    OPERATORS.includes(tokenBuffer[tokenBuffer.length - 1].value as any)
  ) {
    connector = tokenBuffer.pop()?.value as Operator;
  }

  const tokensWithoutWhitespace = trimWhitespace(tokenBuffer);
  if (tokensWithoutWhitespace.length === 0) {
    return;
  }

  const scope = new ParserCompilerScope("parse", parentScope);
  const result: Omit<TokenBufferToExpressionResult, "expression"> & {
    expression?: Expression;
  } = {
    expression: undefined,
    errors: [],
    scope: scope,
  };

  if (tokensWithoutWhitespace.length === 1) {
    if (tokensWithoutWhitespace[0].type === "relationship") {
      const relationship = context.relationships.find(
        (relationship) => relationship.text === tokensWithoutWhitespace[0].text,
      );
      if (relationship?.textSingular) {
        const identifierAttribute = scope.getAttributeByDescription(
          relationship?.textSingular,
          context.namespace,
        );
        if (identifierAttribute) {
          //scope.addAttribute(identifierAttribute);
        }
      }
      return {
        ...result,
        expression: {
          id: generateExpressionId(),
          type: "relationship",
          relationshipId: tokensWithoutWhitespace[0].value.id,
          relationshipReverse: tokensWithoutWhitespace[0].value.reverse,
          text: tokensWithoutWhitespace[0].text,
          operator: connector,
          start: getTokenStartEnd(tokensWithoutWhitespace[0]).start,
          end: getTokenStartEnd(
            tokensWithoutWhitespace[tokensWithoutWhitespace.length - 1],
          ).end,
        },
      };
    }
  }

  state.tokenOffset = getTokenStartEnd(tokensWithoutWhitespace[0]).start;

  let tokensWithoutBrackets = [...tokensWithoutWhitespace];

  while (
    tokensWithoutBrackets.length > 1 &&
    (tokensWithoutBrackets[0].type === "start_arg" ||
      tokensWithoutBrackets[tokensWithoutBrackets.length - 1].type ===
        "end_arg")
  ) {
    if (
      tokensWithoutBrackets[0].pairId ===
      tokensWithoutBrackets[tokensWithoutBrackets.length - 1].pairId
    ) {
      tokensWithoutBrackets = tokensWithoutBrackets.slice(1, -1);
      state.tokenOffset += 1;
    } else {
      // no matching bracket?
      const firstHasNoMatch =
        tokensWithoutBrackets[0].type === "start_arg" &&
        tokensWithoutBrackets[0].pairId === null;
      const lastHasNoMatch =
        tokensWithoutBrackets[tokensWithoutBrackets.length - 1].type ===
          "end_arg" &&
        tokensWithoutBrackets[tokensWithoutBrackets.length - 1].pairId === null;

      if (firstHasNoMatch) {
        tokensWithoutBrackets = tokensWithoutBrackets.slice(1);
        state.tokenOffset += 1;
      }
      if (lastHasNoMatch) {
        tokensWithoutBrackets = tokensWithoutBrackets.slice(0, -1);
      }

      if (!firstHasNoMatch && !lastHasNoMatch) {
        break;
      }
    }
  }

  let text = "";
  let matchText = "";
  for (const token of tokensWithoutBrackets) {
    text += token.text;
    if (token.type === "attribute") {
      matchText += PROCESSED_CHAR.repeat(token.text.length);
    } else {
      matchText += token.text;
    }
  }

  if (context.relationships) {
    const relationship = context.relationships.find(
      (relationship) => relationship.text === text,
    );
    if (relationship) {
      return {
        ...result,
        expression: {
          id: generateExpressionId(),
          text: text,
          type: "relationship",
          relationshipId: relationship.id,
          relationshipReverse: false,
          operator: connector,
          start: getTokenStartEnd(tokensWithoutWhitespace[0]).start,
          end: getTokenStartEnd(
            tokensWithoutWhitespace[tokensWithoutWhitespace.length - 1],
          ).end,
        },
      };
    }
  }

  let description = normalizeText(text);
  let attribute = scope.getAttributeByDescription(
    description,
    context.namespace,
  );

  //console.log(attribute);

  //require("fs").writeFileSync("attributes.json", JSON.stringify(context.attributes, null, 2));
  if (attribute) {
    attribute.meta = attributeMeta;

    return {
      ...result,
      expression: createAttributeExpression(
        attribute,
        context,
        scope,
        tokensWithoutWhitespace,
        connector,
      ),
    };
  }

  /*const verbs = nlp(description).verbs();
  const positiveVerbs = verbs.toPositive().out("offset");
  if (positiveVerbs.length) {
    let positiveDescription = description;
    for (const verb of positiveVerbs) {
      positiveDescription = positiveDescription.slice(0, verb.offset.start) + verb.text + positiveDescription.slice(verb.offset.start + verb.offset.length);
    }
    console.log(description, positiveDescription);
  }*/
  //if (verbs.isNegative().out("array")) {
  //  console.log("NEGATIVE", description);
  //}

  if (tokensWithoutBrackets.length === 1) {
    const token = tokensWithoutBrackets[0];

    if (token.type === "parsed") {
      return;
    }

    if (isLiteralToken(token)) {
      let value = token.value;
      let quoted = false;
      // The literal as written (without quotes), used when the attribute it is compared with has a declared type
      let text: string = token.type === "quoted_phrase" ? token.value : token.text;
      if (token.type === "quoted_phrase") {
        quoted = true;
      } else if (token.type === "number") {
        quoted = value.quoted;
        text = value.text ?? token.text;
        value = value.value;
      }

      return {
        ...result,
        expression: {
          id: generateExpressionId(),
          type: "literal",
          value: value,
          quoted: quoted,
          text: text,
          start: getTokenStartEnd(tokensWithoutWhitespace[0]).start,
          end: getTokenStartEnd(
            tokensWithoutWhitespace[tokensWithoutWhitespace.length - 1],
          ).end,
        },
      };
    }
  }

  const matches: CommandMatch[] = [];
  let fallbackCommandExpression: CommandExpression | undefined;

  for (const command of COMMANDS) {
    for (const definition of command.definition) {
      const result = definition.execForAllGroups(matchText);

      if (result) {
        // Special validation for instance-count: only match if the captured text is a valid relationship
        /*if (command.id === 'instance-count') {
          const capturedText = result[0]?.match;
          if (capturedText) {
            // Check if the captured text matches any known relationship
            const matchesRelationship = context.relationships?.some(rel =>
              rel.text === capturedText || rel.textSingular === capturedText
            );
            if (!matchesRelationship) {
              // Skip this match if it doesn't correspond to a relationship
              continue;
            }
          }
        }*/

        // we need to re-run the regex on matches to get the whole scope of the match, not just the groups
        const rawMatch = result.fullMatch;

        const attributeMatches: string[] = [];

        for (const match of result) {
          match.match = text.slice(match.start, match.end);
          if (scope.getAttributeByDescription(match.match, context.namespace)) {
            attributeMatches.push(match.match);
          }
        }

        matches.push({
          command: command,
          result: result,
          length: rawMatch.length,
          start: rawMatch.index ?? 0,
          end: (rawMatch.index ?? 0) + rawMatch[0].length,
          attributes: attributeMatches,
        });
      }
    }
  }

  if (matches.length) {
    matches.sort((a, b) => {
      // if we fit inside another match, we should be last
      if (
        a.start >= b.start &&
        a.end <= b.end &&
        !(a.start === b.start && a.end === b.end)
      ) {
        return 1;
      }

      // explicit order
      if (a.command.naturalPriority !== b.command.naturalPriority) {
        return b.command.naturalPriority - a.command.naturalPriority;
      }

      if (!b.result[1] || !a.result[1]) {
        return 0;
      }

      // Same precedence: pick rightmost operator (left-to-right evaluation)
      return b.result[1]?.start - a.result[1]?.start;
    });
    let commandMatch: CommandMatch | undefined = undefined;
    while (matches.length > 0) {
      commandMatch = matches.shift();
      if (!commandMatch) {
        break;
      }

      let connector: Operator | undefined;
      if (
        OPERATORS.includes(tokenBuffer[tokenBuffer.length - 1].value as any)
      ) {
        connector = tokenBuffer[tokenBuffer.length - 1].value as Operator;
      }

      // this list of parents can be discarded if this command fails validation
      const newScope = new ParserCompilerScope(`parse[${text}]`, result.scope);

      let parsedIndex = 0;
      const parsedTokens = tokensWithoutBrackets.filter(
        (token) => token.type === "parsed",
      );

      const commandState: CommandState = {
        command: commandMatch.command,
        relationshipId: undefined,
        relationshipReverse: undefined,
        arguments: [],
      };

      for (const arg of commandMatch.result) {
        // is only pre-processed characters
        const argText = removeMatchingBrackets(arg.match);
        if (
          argText &&
          !argText.split("").some((char) => char !== PROCESSED_CHAR)
        ) {
          const parsedToken = parsedTokens[parsedIndex];
          parsedIndex++;
          if (!parsedToken) {
            throw new Error("Expected a parsed token to consume");
          }

          const consumedExpression = parsedToken.value.expression;
          newScope.markExpressionToRemove(consumedExpression);
          addCommandArguments(commandState, scope, [
            { ...consumedExpression, id: generateExpressionId() },
          ]);

          continue;
        }

        // Count parsed tokens that fall within this arg's range and advance parsedIndex
        // so that subsequent pure-parsed args consume the correct parsed token
        if (argText) {
          const argStart = arg.start + state.tokenOffset;
          const argEnd = arg.end + state.tokenOffset;
          while (parsedIndex < parsedTokens.length) {
            const pt = parsedTokens[parsedIndex];
            if (pt.pos >= argStart && pt.pos < argEnd) {
              parsedIndex++;
            } else {
              break;
            }
          }
        }

        const newState: ParseState = {
          ...state,
          tokenOffset: state.tokenOffset + arg.start,
          tokenEnd: undefined,
        };
        const argStartTokenIndex = tokensWithoutWhitespace.findIndex(
          (other) => other.pos >= arg.start + state.tokenOffset,
        );
        const argEndTokenIndex =
          findLastIndex(
            tokensWithoutWhitespace,
            (other) => other.pos < arg.end + state.tokenOffset,
          ) + 1;
        const argTokens =
          argStartTokenIndex >= 0 && argStartTokenIndex < argEndTokenIndex
            ? tokensWithoutWhitespace.slice(
                argStartTokenIndex,
                argEndTokenIndex,
              )
            : [];

        if (!isEqual(argTokens, tokenBuffer)) {
          parseTokens({
            tokens: argTokens,
            context,
            scope: newScope,
            state: {
              ...newState,
              commandState,
            },
            attributeMeta: attributeMeta,
          });
        } else {
          // trying to parse same again
        }
      }

      const command = createCommandExpression(context, result.scope, {
        state: commandState,
        operator: connector,
        natural: true,
        //relationshipId: result.relationshipId,
        start: getTokenStartEnd(tokensWithoutWhitespace[0]).start,
        end:
          state.tokenEnd ??
          getTokenStartEnd(
            tokensWithoutWhitespace[tokensWithoutWhitespace.length - 1],
          ).end,
      });

      if (command.errors?.length) {
        fallbackCommandExpression = command;
        continue;
      }
      newScope.mergeToParent();

      return {
        ...result,
        expression: command,
      };
    }
  }

  // we matched a command in the parent, but now we don't have anything to do with it. let's just consume it and instead be an attribute
  if (text.includes(PROCESSED_CHAR)) {
    for (const token of tokensWithoutBrackets) {
      if (token.type === "parsed") {
        const consumedExpression = token.value.expression;
        result.scope.markExpressionToRemove(consumedExpression);
      }
    }
    text = "";
    for (const token of tokensWithoutBrackets) {
      if (token.type === "parsed") {
        text += token.value.rawString;
      } else {
        text += token.text;
      }
    }
    description = normalizeText(text);
  }

  if (!description) {
    return;
  }

  let attributeId = (attribute as any)?.id;
  if (!attributeId) {
    const entity = context.matchEntity(description) || "global";
    attributeId = getGuidForDescription(context, description);
    attribute = {
      id: attributeId,
      description: description,
      entity: entity,
      namespace: context.namespace,
      generated: true,
      meta: attributeMeta,
    };
  }

  if (!attribute) {
    throw new Error("Expected attribute to exist");
  }

  // Prefer a previously matched command-with-errors when the attribute text itself
  // is invalid. Do this before creating the attribute expression to avoid mutating
  // parser scope with an attribute we are about to discard.
  if (
    fallbackCommandExpression &&
    hasInvalidAttributeDescription(attribute.description || "")
  ) {
    return {
      ...result,
      expression: fallbackCommandExpression,
    };
  }

  const attributeExpression = createAttributeExpression(
    attribute,
    context,
    scope,
    tokensWithoutWhitespace,
    connector,
  );
  // If attribute parsing produces a validation error, prefer a previously matched command
  // (with its own errors) so malformed operator usage like trailing "=" is surfaced as
  // command-level feedback instead of only an invalid attribute.
  if (attributeExpression.errors?.length && fallbackCommandExpression) {
    return {
      ...result,
      expression: fallbackCommandExpression,
    };
  }

  return {
    ...result,
    expression: attributeExpression,
  };
};

interface CommandExpressionCreateOptions {
  id?: string;
  state: CommandState;
  operator?: Operator;
  start: number;
  end: number;
  natural: boolean;
  errors?: CompilerError[];
}

const expressionToText = (expression: Expression): string | undefined => {
  if (!expression) {
    return undefined;
  }
  if (expression.type === "attribute") {
    return expression.text;
  }
  if (expression.type === "relationship") {
    return expression.text;
  }
  return undefined;
};

const createCommandExpression = (
  context: CompilerContext,
  scope: ParserCompilerScope,
  options: CommandExpressionCreateOptions,
): CommandExpression => {
  const { state, id, operator, start, end, natural } = options;

  const { command } = state;

  const filteredArguments = state.arguments.filter(Boolean) as Expression[];

  for (let i = 0; i < filteredArguments.length; i++) {
    const arg = filteredArguments[i];

    if (
      command.id === "for" ||
      command.id === INFER_INSTANCE_COMMAND.id ||
      command.id === INFER_INSTANCES_COMMAND.id
    ) {
      if (arg.type === "relationship") {
        const relationship = context.relationships.find(
          (relationship) => relationship.id === arg.relationshipId,
        );
        if (relationship?.text) {
          const attribute = scope.getAttributeByDescription(
            relationship.text,
            context.namespace,
          );
          let entity = relationship.name;
          if (!entity) {
            const containingRelationship = context.relationships.find(
              (other) =>
                other.target === relationship.target && other.containment,
            );
            if (containingRelationship) {
              entity = containingRelationship.name;
            }

            if (!entity) {
              throw new Error("Expected entity to exist");
            }
          }

          if (attribute) {
            attribute.entity = entity;
          }

          scope.addAttribute(
            attribute || {
              id: getGuidForDescription(context, relationship.text),
              description: relationship.text,
              entity: entity,
              generated: true,
            },
          );
        }
      }
    }
  }

  const expression: CommandExpression = {
    id: id ?? generateExpressionId(),
    type: "command",
    command,
    errors: options?.errors,
    operator: operator,
    arguments: filteredArguments,
    start: start,
    end: end,
    natural,
    relationshipId: state.relationshipId,
    relationshipReverse: state.relationshipReverse,
  };

  expression.arguments = removeMarkedForDeletion(scope, expression.arguments);
  checkCommandForErrors(expression);

  return expression;
};

const getExpressionContents = (element: Expression): string => {
  if (element.type === "command") {
    return element.command.id;
  }
  return expressionToText(element) || element.type;
};

const checkCommandForErrors = (
  expression: CommandExpression,
): CompilerError[] | undefined => {
  let errors: CompilerError[] | undefined = expression.errors;
  if (
    expression &&
    expression.arguments.length < expression.command.minimumArguments
  ) {
    errors = addError(errors, {
      type: COMPILER_ERROR_TYPES.INVALID_COMMAND_ARGUMENT_COUNT.id,
      message: `Expected at least ${expression.command.minimumArguments} arguments for command '${expression.command.id}'`,
    });
  }
  const maxArgs =
    expression?.command.maximumArguments ??
    expression?.command.minimumArguments;
  if (typeof maxArgs === "number" && expression.arguments.length > maxArgs) {
    errors = addError(errors, {
      type: COMPILER_ERROR_TYPES.INVALID_COMMAND_ARGUMENT_COUNT.id,
      message: `Expected at most ${maxArgs} arguments for command '${expression.command.id}'`,
    });
  }

  for (let i = 0; i < expression.arguments.length; i++) {
    const arg = expression.command.arguments[i];
    if (!arg) {
      continue;
    }
    const element = expression.arguments[i];
    for (const type of arg.types) {
      if (type === "relationship" && element.type !== "relationship") {
        errors = addError(
          errors,
          createMissingRelationshipError(
            getExpressionContents(element),
            expression.command.id,
            arg.name,
            i,
          ),
        );
      } else if (type === "alias") {
        if (element.type !== "alias") {
          errors = addError(errors, {
            type: COMPILER_ERROR_TYPES.INVALID_ALIAS_ARGUMENT.id,
            message: `Expected an alias for argument ${i + 1} of command '${expression.command.id}'`,
          });
        } else if (!element.alias) {
          errors = addError(errors, {
            type: COMPILER_ERROR_TYPES.INVALID_ALIAS_ARGUMENT.id,
            message: `Expected an aliased attribute for argument ${i + 1} of command '${expression.command.id}'`,
          });
        }
      }
    }
  }

  if (errors?.length) {
  }

  expression.errors = errors;
  return errors;
};

interface ParseTokensResult {
  expressions: Expression[];
  end?: number;
  errors: CompilerError[];
  scope: ParserCompilerScope;
  state: ParseState;
}

export type ParseOptions = {
  input: string;
  attributeMeta?: AttributeMeta;
} & (
  | {
      relationships?: Relationship[];
      graph?: any;
      attributes?: AttributesOption;
      context?: never;
      scope?: never;
      currentDocument?: Document;
      documentTree?: any;
    }
  | {
      context: CompilerContext;
      scope?: CompilerScope;
    }
);

export interface ParseResult {
  attributes: Attribute[];
  expressions: Expression[];
  scope: CompilerScope;
  context: CompilerContext;
  errors: CompilerError[];
}

const removeMarkedForDeletionInternal = (
  scope: ParserCompilerScope,
  expressions: Expression[],
  usedAttributes: Set<string>,
): Expression[] => {
  const markedForDeletion = scope.getMarkedExpressionsToRemove();
  return expressions.filter((expression) => {
    const toDelete = markedForDeletion[expression.id];
    if (toDelete) {
      return false;
    }
    if (expression.type === "attribute") {
      usedAttributes.add(expression.attributeId);
    }
    if (expression.type === "command") {
      expression.arguments = removeMarkedForDeletionInternal(
        scope,
        expression.arguments,
        usedAttributes,
      );
    }
    return true;
  });
};

const removeMarkedForDeletion = (
  scope: ParserCompilerScope,
  expressions: Expression[],
): Expression[] => {
  const usedAttributes = new Set<string>();

  const result = removeMarkedForDeletionInternal(
    scope,
    expressions,
    usedAttributes,
  );

  iterateExpressions(
    Object.values(scope.getMarkedExpressionsToRemove()),
    (expression) => {
      if (expression.type === "attribute") {
        if (!usedAttributes.has(expression.attributeId)) {
          scope.removeAttributeById(expression.attributeId);
        }
      }
    },
  );

  return result;
};

const findExpression = (
  filter: (expression: Expression) => boolean,
  expressions: Expression[],
): Expression | undefined => {
  for (const expression of expressions) {
    if (filter(expression)) {
      return expression;
    }
    if (expression.type === "command") {
      const result = findExpression(filter, expression.arguments);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
};

const collectExpressionErrors = (
  expressions: Expression[],
): CompilerError[] => {
  const errors: CompilerError[] = [];
  for (const expression of expressions) {
    if (expression.errors?.length) {
      errors.push(...expression.errors);
    }
    if (expression.type === "command") {
      errors.push(...collectExpressionErrors(expression.arguments));
    }
  }
  return errors;
};

// public interface -- for syntax highlighting
export const parse = (options: ParseOptions): ParseResult => {
  const context = options.context
    ? options.context
    : createCompilerContext(options);
  const scope = new ParserCompilerScope(
    "parse",
    options.scope ?? context.rootScope,
  );
  const tokens = options.input.trim().length
    ? context.tokenizer.tokenize(options.input)
    : [];
  const result = parseTokens({
    tokens,
    context,
    scope,
    attributeMeta: options.attributeMeta,
  });

  result.expressions = removeMarkedForDeletion(scope, result.expressions);
  if (result.state.startArgs.length) {
    for (const startArg of result.state.startArgs) {
      if (startArg?.command && !startArg.closed) {
        const commandExpression = findExpression(
          (expression) => expression.id === startArg.expressionId,
          result.expressions,
        );
        if (commandExpression) {
          const error = {
            type: COMPILER_ERROR_TYPES.SYNTAX_ERROR.id,
            message: "Unclosed bracket",
          };
          commandExpression.errors = addError(commandExpression.errors, error);
          result.errors.push(error);
        }
        //scope.findExpression(command.id);
      }
    }
    //result.errors.push({
    //  message: "Unclosed bracket",
    //});
  }

  const expressionErrors = collectExpressionErrors(result.expressions);
  if (expressionErrors.length) {
    result.errors.push(...expressionErrors);
  }

  if (!result.errors.length) {
    scope.mergeToParent();
  }

  return {
    expressions: result.expressions,
    attributes: scope
      .getCapturedAttributes()
      .filter(
        (attribute) =>
          !hasInvalidAttributeDescription(attribute.description || ""),
      ),
    scope,
    context,
    errors: result.errors,
  };
};

export const addError = (
  errors: CompilerError[] | undefined,
  error: CompilerError,
) => {
  const resolvedErrors = errors || [];
  resolvedErrors.push(error);
  return resolvedErrors;
};

interface ParseState {
  tokenOffset: number;
  // if we have already parsed further tokens and this was thier end point
  tokenEnd?: number;
  // whether we are currently parsing an arguments and need to look out for an end arg
  startArgs: Array<StartArg>;
  insideComment: boolean[];
  commandState?: CommandState;
}

interface StartArg {
  expressionId: string | undefined;
  command: Command | undefined;
  pairId: number;
  closed: boolean;
}

interface CommandMatch {
  command: Command;
  start: number;
  end: number;
  length: number;
  result: MultiRegExpMatch[];
  attributes: string[];
}

function addTokenBufferResult(
  result: ParseTokensResult,
  tokenBufferResult: TokenBufferToExpressionResult,
) {
  if (tokenBufferResult.expression) {
    result.expressions.push(tokenBufferResult.expression);
  }
  if (tokenBufferResult.errors) {
    result.errors.push(...tokenBufferResult.errors);
  }
  tokenBufferResult.scope.mergeToParent();

  if (result.state.commandState) {
    addCommandArguments(result.state.commandState, result.scope, [
      tokenBufferResult.expression,
    ]);
  }
}

export interface ParseTokensOptions {
  tokens: Token[] | TokenQueue;
  context: CompilerContext;
  scope: ParserCompilerScope;
  state?: ParseState;
  attributeMeta?: AttributeMeta;
}

const parseTokens = (options: ParseTokensOptions): ParseTokensResult => {
  const { tokens, context, scope, state, attributeMeta } = options;
  const resolvedState: ParseState = {
    tokenOffset: 0,
    startArgs: [],
    insideComment: [],
    ...state,
  };

  let parsedArgs = 0;

  const result: ParseTokensResult = {
    expressions: [],
    errors: [],
    scope: scope,
    state: resolvedState,
  };

  const queue = tokens instanceof TokenQueue ? tokens : new TokenQueue(tokens);
  let tokenBuffer: Token[] = [];

  let inComment = false;

  while (queue.hasNext()) {
    const token = queue.pop();
    if (!token) {
      console.error("No token but has next was true?", queue);
      break;
    }

    inComment = resolvedState.insideComment.length > 0;
    if (token.type === "start_comment") {
      resolvedState.insideComment.push(true);
    } else if (token.type === "end_comment") {
      result.expressions.push({
        id: generateExpressionId(),
        type: "comment",
        text: tokenBuffer.map((token) => token.text).join(""),
        start: getTokenStartEnd(tokenBuffer[0]).start,
        end: getTokenStartEnd(tokenBuffer[tokenBuffer.length - 1]).end,
      });
      tokenBuffer = [];
      resolvedState.insideComment.pop();
    } else if (inComment) {
      tokenBuffer.push(token);
    } else if (token.type === "start_arg") {
      token.pairId = resolvedState.startArgs.length;
      const nameTokens: Token[] = [];
      let nameTokenStart = 0;
      for (let i = tokenBuffer.length - 1; i >= 0; i--) {
        const token = tokenBuffer[i];
        if (token.type === "ws") {
          continue;
        }

        if (
          token.type !== "word" &&
          token.type !== "attribute" &&
          token.type !== "uncertain"
        ) {
          break;
        }
        nameTokens.unshift(token);
        nameTokenStart = i;
      }
      if (nameTokens.length === 0) {
        tokenBuffer.push(token);
        resolvedState.startArgs.push({
          expressionId: undefined,
          command: undefined,
          pairId: resolvedState.startArgs.length,
          closed: false,
        });
        continue;
      }

      const nameLiteral = nameTokens.map((token) => token.text).join("");

      const command = findCommand(nameLiteral);
      if (!command) {
        tokenBuffer.push(token);
        resolvedState.startArgs.push({
          expressionId: undefined,
          command: undefined,
          pairId: resolvedState.startArgs.length,
          closed: false,
        });
        continue;
      }

      const expressionId = generateExpressionId();
      resolvedState.startArgs.push({
        expressionId: expressionId,
        command,
        pairId: token.pairId,
        closed: false,
      });
      resolvedState.tokenOffset = getTokenStartEnd(token).end;

      const commandState: CommandState = {
        command,
        relationshipId: undefined,
        relationshipReverse: undefined,
        arguments: [],
      };

      const argsResult = parseTokens({
        tokens: queue,
        context,
        scope,
        state: {
          ...resolvedState,
          commandState,
        },
        attributeMeta: attributeMeta,
      });
      const endTokenIndex = queue.index;
      const rawString = queue.tokens
        .slice(nameTokenStart, endTokenIndex)
        .map((token) => {
          if (token.type === "parsed") {
            return token.value.rawString;
          }
          return token.text;
        })
        .join("");

      const start = getTokenStartEnd(nameTokens[0]).start;

      const commandExpression = createCommandExpression(context, result.scope, {
        id: expressionId,
        state: commandState,
        start: start,
        natural: false,
        end:
          argsResult.end ??
          argsResult.expressions.reduce(
            (end, expression) => {
              return Math.max(end, expression.end);
            },
            start + command.id.length + 2,
          ),
      });
      if (commandExpression.errors) {
        result.errors.push(...commandExpression.errors);
      }

      tokenBuffer.splice(nameTokenStart, tokenBuffer.length - nameTokenStart);

      if (rawString.includes(PROCESSED_CHAR)) {
        throw new Error("Unexpected processed char in raw string");
      }

      tokenBuffer.push({
        type: "parsed",
        value: { rawString, expression: commandExpression },
        text: PROCESSED_CHAR.repeat(
          commandExpression.end - commandExpression.start,
        ),
        column: commandExpression.start + 1,
        pos: commandExpression.start,
        line: 0,
      });
      result.expressions.push(commandExpression);
      if (resolvedState.commandState) {
        addCommandArguments(resolvedState.commandState, scope, [
          commandExpression,
        ]);
      }
    } else if (token.type === "end_arg") {
      let lastUnclosedStart;
      for (const startArg of resolvedState.startArgs) {
        if (!startArg.closed) {
          lastUnclosedStart = startArg;
        }
      }

      token.pairId = lastUnclosedStart?.pairId ?? null;
      if (lastUnclosedStart?.command) {
        lastUnclosedStart.closed = true;
        result.end = getTokenStartEnd(token).end;
        break;
      }
      if (lastUnclosedStart) {
        lastUnclosedStart.closed = true;
      }
      tokenBuffer.push(token);

      const startTokenIndex = findLastIndex(
        tokenBuffer,
        (other: Token) =>
          other.type === "start_arg" && token.pairId === other.pairId,
      );

      const rawString = tokenBuffer
        .slice(startTokenIndex)
        .map((token) => {
          if (token.type === "parsed") {
            return token.value.rawString;
          }
          return token.text;
        })
        .join("");

      const possiblyCommand = tokenBufferToExpression(
        scope,
        tokenBuffer.slice(startTokenIndex),
        context,
        {
          ...resolvedState,
        },
        attributeMeta,
        false, // isCommandArgument = false for end argument parsing
      );
      if (possiblyCommand?.expression.type === "command") {
        tokenBuffer = [
          ...tokenBuffer.slice(0, startTokenIndex),
          {
            type: "parsed",
            value: { rawString, expression: possiblyCommand.expression },
            text: PROCESSED_CHAR.repeat(
              possiblyCommand.expression.end - possiblyCommand.expression.start,
            ),
            column: possiblyCommand.expression.start + 1,
            pos: possiblyCommand.expression.start,
            line: 0,
          },
        ];
        addTokenBufferResult(result, possiblyCommand);
        //resolvedState.tokenOffset = possiblyCommand.start;
      }
    } else if (token.type === "separator") {
      // The seperator is not inside a command - it's just a comma and is part of the attribute
      let lastUnclosedStart;
      for (const startArg of resolvedState.startArgs) {
        if (!startArg.closed) {
          lastUnclosedStart = startArg;
        }
      }
      if (!lastUnclosedStart?.command) {
        if (tokenBuffer.length > 0) {
          tokenBuffer.push(token);
        }
      } else if (tokenBuffer.length > 0) {
        const commandState = resolvedState.commandState;
        if (commandState) {
          const argResult = tokenBufferToExpression(
            scope,
            tokenBuffer,
            context,
            {
              ...resolvedState,
            },
            attributeMeta,
            true, // isCommandArgument = true when parsing command arguments
          );
          if (!argResult) {
            resolvedState.tokenOffset = getTokenStartEnd(token).end;
            tokenBuffer = [];
          } else {
            //console.log('commandState.command.maximumArguments', commandState.command)
            if (
              parsedArgs >= commandState.command.maximumArguments - 1 &&
              tokenBuffer.length > 0
            ) {
              // we have all the arguments we need, this is just a separator
              tokenBuffer.push(token);
            } else {
              addTokenBufferResult(result, argResult);
              resolvedState.tokenOffset = getTokenStartEnd(token).end;
              tokenBuffer = [];
              parsedArgs++;
            }
          }
        } else {
          tokenBuffer.push(token);
        }
      }
    } else if (
      token.type === "number" ||
      token.type === "quoted_phrase" ||
      token.type === "boolean" ||
      token.type === "date"
    ) {
      tokenBuffer.push(token);
    } else if (token.type === "relationship") {
      tokenBuffer.push(token);
    } else {
      tokenBuffer.push(token);
    }
  }

  if (hasNonWhitespaceTokens(tokenBuffer)) {
    if (inComment) {
      result.expressions.push({
        type: "comment",
        text: tokenBuffer.map((token) => token.text).join(""),
        start: getTokenStartEnd(tokenBuffer[0]).start,
        end: getTokenStartEnd(tokenBuffer[tokenBuffer.length - 1]).end,
        id: generateExpressionId(),
      });
    } else {
      resolvedState.tokenOffset = getTokenStartEnd(tokenBuffer[0]).start;
      const tokenBufferResult = tokenBufferToExpression(
        scope,
        tokenBuffer,
        context,
        {
          ...resolvedState,
        },
        attributeMeta,
        false, // isCommandArgument = false for non-command parsing
      );

      if (tokenBufferResult) {
        addTokenBufferResult(result, tokenBufferResult);
      }
    }
  }

  return result;
};

export const hasNonWhitespaceTokens = (tokens: Token[]): boolean => {
  return tokens.some((token) => token.type !== "ws");
};

export const getTokenStartEnd = (
  token: Token,
): {
  start: number;
  end: number;
} => {
  return { start: token.pos, end: token.pos + token.text.length };
};
